import React, { useState, useEffect, useCallback } from "react";
import { 
    Container, Col, Row, Button, Form, Card, 
    Table, Pagination, InputGroup, Spinner, Modal,  // Add this import
    Alert, Toast
} from "react-bootstrap";
import { debounce } from 'lodash';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import "../css/loanApplications.scss";
import GLOBALVAR from "../constants";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import ESignButton from "../components/misc/EsignButton";
import ApproveButton from "../components/misc/ApproveButton";

// Move formatDate outside both components so it can be shared
const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

function LoanApplicationPage() {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [status, setStatus] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showViewModal, setShowViewModal] = useState(false);  // Add these new states
    const [selectedApplication, setSelectedApplication] = useState(null);  // Add these new states
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const fetchApplications = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: 10,
                search: searchTerm,
                status,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString()
            };

            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-applications`, { params });
            setApplications(response.data.items);
            setTotalPages(response.data.totalPages);
            setCurrentPage(response.data.currentPage);
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, status, startDate, endDate]);

    // Debounced search function
    const debouncedSearch = debounce((searchValue) => {
        setSearchTerm(searchValue);
        setCurrentPage(1);
        fetchApplications(1);
    }, 500);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchApplications(page);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        debouncedSearch(value);

        // Handle autosuggest
        if (value.length > 2) {
            const filtered = applications.filter(app => 
                `${app.firstName} ${app.lastName}`.toLowerCase().includes(value.toLowerCase()) ||
                app.email.toLowerCase().includes(value.toLowerCase())
            );
            setSuggestions(filtered.slice(0, 5));
        } else {
            setSuggestions([]);
        }
    };

    const handleViewApplication = (application) => {  // Add this handler
        setSelectedApplication(application);
        setShowViewModal(true);
    };

    const showNotification = (message) => {
        setToastMessage(message);
        setShowToast(true);
    };

    const handleUpdate = async (updatedApplication) => {
        // Update the local state immediately for better UX
        setApplications(prevApplications => 
            prevApplications.map(app => 
                app.id === updatedApplication.id ? updatedApplication : app
            )
        );

        // Show notification
        showNotification('Application updated successfully');

        // Refresh the data from server
        fetchApplications();
    };

    const AutoHideToast = ({ message }) => (
        <div
            style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999
            }}
        >
            <Toast 
                onClose={() => setShowToast(false)} 
                show={showToast} 
                delay={3000} 
                autohide
                animation={true}
            >
                <Toast.Body className="bg-success text-white">
                    <i className="fas fa-check-circle me-2"></i>
                    {message}
                </Toast.Body>
            </Toast>
        </div>
    );

    return (
        <Container fluid className="loan-applications-container py-4">
            <AutoHideToast message={toastMessage} />
            <Card>
                <Card.Header className="bg-primary text-white">
                    <h2 className="mb-0">Loan Applications</h2>
                </Card.Header>
                <Card.Body>
                    <Row className="search-section g-3">
                        <Col lg={4} md={6}>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <i className="fas fa-search"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by name or email..."
                                    onChange={handleSearch}
                                    className="border-start-0"
                                />
                                {suggestions.length > 0 && (
                                    <div className="suggestions-dropdown">
                                        {suggestions.map((suggestion, index) => (
                                            <div 
                                                key={index} 
                                                className="suggestion-item"
                                                onClick={() => {
                                                    setSearchTerm(`${suggestion.firstName} ${suggestion.lastName}`);
                                                    setSuggestions([]);
                                                    fetchApplications(1);
                                                }}
                                            >
                                                {suggestion.firstName} {suggestion.lastName} - {suggestion.email}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </InputGroup>
                        </Col>
                        <Col lg={3} md={6}>
                            <Form.Select 
                                value={status} 
                                onChange={(e) => setStatus(e.target.value)}
                                className="h-100"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </Form.Select>
                        </Col>
                        <Col lg={5} md={12}>
                            <Row className="g-2">
                                <Col md={6} className="date-picker-wrapper">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={setStartDate}
                                        className="form-control"
                                        placeholderText="Start Date"
                                    />
                                </Col>
                                <Col md={6} className="date-picker-wrapper">
                                    <DatePicker
                                        selected={endDate}
                                        onChange={setEndDate}
                                        className="form-control"
                                        placeholderText="End Date"
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Loan Amount</th>
                                            <th>Status</th>
                                            <th>Date Applied</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {applications.map((app) => (
                                            <tr key={app.id}>
                                                <td>
                                                    {app.status === 'approved' ? (
                                                        <a 
                                                            href={`${window.location.origin}/applicant/${app.applicationsId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-primary text-decoration-none"
                                                        >
                                                            {`${app.firstName} ${app.lastName}`}
                                                        </a>
                                                    ) : (
                                                        `${app.firstName} ${app.lastName}`
                                                    )}
                                                </td>
                                                <td>{app.email}</td>
                                                <td>₱{Number(app.loanAmount).toLocaleString()}</td>
                                                <td>
                                                    <span className={`status-badge status-${app.status}`}>
                                                        {app.status}
                                                    </span>
                                                </td>
                                                <td>{formatDate(app.createdAt)}</td>
                                                <td>
                                                    <div className="d-flex gap-2 action-buttons">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={() => handleViewApplication(app)}
                                                            className="btn-icon-only"
                                                            title="View Details"
                                                        >
                                                            <span className="d-none d-md-inline me-1">View</span>
                                                            <i className="fas fa-eye"></i>
                                                        </Button>
                                                        {app.status === 'pending' && (
                                                            <ESignButton 
                                                                formData={app} 
                                                                isCompact={window.innerWidth < 768} 
                                                                onSuccess={showNotification}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted">
                                    Showing {applications.length} of {totalPages * 10} entries
                                </div>
                                <Pagination>
                                    <Pagination.First 
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                    />
                                    {[...Array(totalPages)].map((_, index) => (
                                        <Pagination.Item
                                            key={index + 1}
                                            active={currentPage === index + 1}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Last
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {showViewModal && selectedApplication && (
                <ViewModal 
                    show={showViewModal}
                    onHide={() => setShowViewModal(false)}
                    application={selectedApplication}
                    onUpdate={handleUpdate}
                    showNotification={showNotification}
                />
            )}
        </Container>
    );
}

// First, create a new StatusButton component above the ViewModal component
const StatusButton = ({ application, onUpdate, showNotification, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(application.status);
  const [isCompact, setIsCompact] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update the handleStatusUpdate function in StatusButton component
  const handleStatusUpdate = async (newStatus) => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${GLOBALVAR['SERVER_ROOT']}/api/loan-application/${application.id}`,
        { ...application, status: newStatus }
      );
      if (response.data) {
        setCurrentStatus(newStatus); // Update local state immediately
        onUpdate(response.data.application);
        showNotification(`Application status updated to ${newStatus}`);
        onClose(); // Close the modal after successful update

        // If status is being set to rejected, send rejection emails
        if (newStatus === 'rejected') {
            const submissionDate = new Date(application.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: '2-digit'
            });

            await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/templates/send/loan_rejected`, {
                recipients: [
                    {
                        email: application.email,
                        data: {
                            applicant_name: `${application.firstName} ${application.lastName}`,
                            submission_date: submissionDate,
                            loan_amount: parseFloat(application.loanAmount).toLocaleString(),
                            loan_term: application.loanTerm,
                            comaker_name: `${application.coMakerFirstName} ${application.coMakerLastName}`
                        }
                    },
                    {
                        email: application.coMakerEmail,
                        data: {
                            applicant_name: `${application.firstName} ${application.lastName}`,
                            submission_date: submissionDate,
                            loan_amount: parseFloat(application.loanAmount).toLocaleString(),
                            loan_term: application.loanTerm,
                            comaker_name: `${application.coMakerFirstName} ${application.coMakerLastName}`
                        }
                    }
                ]
            });
        }
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use currentStatus instead of application.status for rendering
  if (currentStatus === 'rejected') {
    return (
      <Button
        variant="warning"
        size="sm"
        onClick={() => handleStatusUpdate('pending')}
        disabled={loading}
        className="btn-icon-flex"
      >
        {loading ? <Spinner animation="border" size="sm" /> : (
          <>
            <i className="fas fa-undo"></i>
            {!isCompact && <span className="ms-1">Mark as Pending</span>}
          </>
        )}
      </Button>
    );
  }

  // Update the ApproveButton usage in StatusButton component
  if (currentStatus === 'pending') {
    return (
      <div className="d-flex gap-2">
        <ApproveButton 
          formData={application} 
          onUpdate={onUpdate}
          onSuccess={(message) => {
            showNotification(message);
            onClose(); // Close the ViewModal after successful approval
          }}
          isCompact={isCompact}
        />
        <Button
          variant="danger"
          size="sm"
          onClick={() => handleStatusUpdate('rejected')}
          disabled={loading}
          className="btn-icon-flex"
        >
          {loading ? <Spinner animation="border" size="sm" /> : (
            <>
              <i className="fas fa-times"></i>
              {!isCompact && <span className="ms-1">Reject</span>}
            </>
          )}
        </Button>
      </div>
    );
  }

  return null;
};

const ViewModal = ({ application, show, onHide, onUpdate, showNotification }) => {
  const [editMode, setEditMode] = useState({});
  const [editedData, setEditedData] = useState(application);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [localApplication, setLocalApplication] = useState(application);

  // Update local state when application changes
  useEffect(() => {
    setLocalApplication(application);
    setEditedData(application);
  }, [application]);

  const handleEdit = (section) => {
    setEditMode(prev => ({ ...prev, [section]: true }));
  };

  const handleCancel = (section) => {
    setEditMode(prev => ({ ...prev, [section]: false }));
    // Reset only the fields in the current section
    const sectionFields = getSectionFields(section);
    const resetData = { ...editedData };
    sectionFields.forEach(field => {
      resetData[field] = application[field];
    });
    setEditedData(resetData);
  };

  const handleChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (section) => {
    setIsSubmitting(true);
    setError(null);

    try {
        const sectionFields = getSectionFields(section);
        const changedData = {};
        sectionFields.forEach(field => {
            if (editedData[field] !== localApplication[field]) {
                changedData[field] = editedData[field];
            }
        });

        if (Object.keys(changedData).length === 0) {
            setEditMode(prev => ({ ...prev, [section]: false }));
            return;
        }

        const response = await axios.put(
            `${GLOBALVAR['SERVER_ROOT']}/api/loan-application/${application.id}`,
            {
                ...changedData,
                id: application.id
            }
        );

        if (response.data?.application) {
            // Update all local states
            const updatedData = response.data.application;
            setLocalApplication(updatedData);
            setEditedData(updatedData);
            onUpdate(updatedData);
            
            // Turn off edit mode for this section
            setEditMode(prev => ({ ...prev, [section]: false }));
            showNotification('Section updated successfully');
        }
    } catch (err) {
        console.error('Update error:', err);
        setError(err.response?.data?.message || 'Failed to update application');
        showNotification('Failed to update section', 'danger');
    } finally {
        setIsSubmitting(false);
    }
  };

  // Helper function to get fields for each section
  const getSectionFields = (section) => {
    const sectionFieldMap = {
      borrower: ['firstName', 'middleName', 'lastName', 'birthday', 'gender', 'civilStatus'],
      contact: ['mobileNumber1', 'mobileNumber2', 'email'],
      address: ['houseStreet', 'barangay', 'municipality', 'province', 'zipCode'],
      employment: ['sourceOfIncome', 'natureOfWork', 'employer', 'jobPosition', 'monthlyIncome'],
      loan: ['loanAmount', 'loanTerm', 'loanPurpose', 'motherMaidenName', 'referredBy'],
      spouse: ['spouseName', 'spouseEmployer', 'spouseContact'],
      coMaker: ['coMakerFirstName', 'coMakerMiddleName', 'coMakerLastName', 'coMakerContact', 'coMakerEmail'],
      coMakerAddress: ['coMakerHouseStreet', 'coMakerBarangay', 'coMakerMunicipality', 'coMakerProvince', 'coMakerZipCode'],
      coMakerEmployment: ['coMakerSourceOfIncome', 'coMakerNatureOfWork', 'coMakerEmployer', 'coMakerJobPosition', 'coMakerMonthlyIncome']
    };
    return sectionFieldMap[section] || [];
  };

  const renderEditableField = (label, field, value, colSize = 4, type = 'text') => (
    <Col md={colSize} className="mb-3">
      <Form.Group>
        <Form.Label className="small text-muted">{label}</Form.Label>
        <Form.Control
          type={type}
          value={value || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          disabled={isSubmitting}
        />
      </Form.Group>
    </Col>
  );

  const renderSectionHeader = (title, section) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="text-primary mb-0">{title}</h6>
      {application.status === 'pending' && (
        editMode[section] ? (
          <div className="btn-group">
            <Button
              variant="success"
              size="sm"
              onClick={() => handleSave(section)}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faSave} /> Save
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={() => handleCancel(section)}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faTimes} /> Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleEdit(section)}
            className="text-primary"
          >
            <FontAwesomeIcon icon={faPencilAlt} /> Edit
          </Button>
        )
      )}
    </div>
  );

  const renderSection = (title, section, content) => (
    <div className="detail-section mb-4">
      {renderSectionHeader(title, section)}
      <Row>
        {editMode[section] ? (
          content(editedData)
        ) : (
          content(localApplication)
        )}
      </Row>
    </div>
  );

  const formatCurrency = (amount) => {
    return `₱${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const renderField = (label, value, colSize = 4) => (
    <Col md={colSize} className="mb-3">
      <div className="field-label text-muted small">{label}</div>
      <div className="field-value">{value || '—'}</div>
    </Col>
  );

  // Example of how to use the new renderSection
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          Loan Application Details
          <div className="small text-muted">
            Application ID: {application?.applicationsId || 'N/A'}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="application-details">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="status-section mb-4">
          <span className={`status-badge status-${application.status}`}>
            {application.status.toUpperCase()}
          </span>
          <span className="ms-3 text-muted">
            Submitted on {formatDate(application.createdAt)}
          </span>
        </div>

        {renderSection("Borrower Information", "borrower", (data) => (
          <>
            {editMode.borrower ? (
              <>
                {renderEditableField("First Name", "firstName", data.firstName)}
                {renderEditableField("Middle Name", "middleName", data.middleName)}
                {renderEditableField("Last Name", "lastName", data.lastName)}
                {renderEditableField("Birthday", "birthday", data.birthday, 4, "date")}
                {renderEditableField("Gender", "gender", data.gender, 4, "select", [
                  "Male", "Female"
                ])}
                {renderEditableField("Civil Status", "civilStatus", data.civilStatus, 4, "select", [
                  "Single", "Married", "Widowed", "Separated"
                ])}
              </>
            ) : (
              <>
                {renderField("First Name", data.firstName)}
                {renderField("Middle Name", data.middleName)}
                {renderField("Last Name", data.lastName)}
                {renderField("Birthday", formatDate(data.birthday))}
                {renderField("Gender", data.gender)}
                {renderField("Civil Status", data.civilStatus)}
              </>
            )}
          </>
        ))}

        {renderSection("Contact Information", "contact", (data) => (
          <>
            {editMode.contact ? (
              <>
                {renderEditableField("Mobile Number 1", "mobileNumber1", data.mobileNumber1)}
                {renderEditableField("Mobile Number 2", "mobileNumber2", data.mobileNumber2)}
                {renderEditableField("Email", "email", data.email)}
              </>
            ) : (
              <>
                {renderField("Mobile Number 1", data.mobileNumber1)}
                {renderField("Mobile Number 2", data.mobileNumber2)}
                {renderField("Email", data.email)}
              </>
            )}
          </>
        ))}

        {renderSection("Address", "address", (data) => (
          <>
            {editMode.address ? (
              <>
                {renderEditableField("House/Street", "houseStreet", data.houseStreet, 6)}
                {renderEditableField("Barangay", "barangay", data.barangay, 6)}
                {renderEditableField("Municipality", "municipality", data.municipality, 4)}
                {renderEditableField("Province", "province", data.province, 4)}
                {renderEditableField("Zip Code", "zipCode", data.zipCode, 4)}
              </>
            ) : (
              <>
                {renderField("House/Street", data.houseStreet, 6)}
                {renderField("Barangay", data.barangay, 6)}
                {renderField("Municipality", data.municipality, 4)}
                {renderField("Province", data.province, 4)}
                {renderField("Zip Code", data.zipCode, 4)}
              </>
            )}
          </>
        ))}

        {renderSection("Employment Details", "employment", (data) => (
          <>
            {editMode.employment ? (
              <>
                {renderEditableField("Source of Income", "sourceOfIncome", data.sourceOfIncome)}
                {renderEditableField("Nature of Work", "natureOfWork", data.natureOfWork)}
                {renderEditableField("Employer", "employer", data.employer)}
                {renderEditableField("Job Position", "jobPosition", data.jobPosition)}
                {renderEditableField("Monthly Income", "monthlyIncome", data.monthlyIncome)}
              </>
            ) : (
              <>
                {renderField("Source of Income", data.sourceOfIncome)}
                {renderField("Nature of Work", data.natureOfWork)}
                {renderField("Employer", data.employer)}
                {renderField("Job Position", data.jobPosition)}
                {renderField("Monthly Income", formatCurrency(data.monthlyIncome))}
              </>
            )}
          </>
        ))}

        {renderSection("Loan Details", "loan", (data) => (
          <>
            {editMode.loan ? (
              <>
                {renderEditableField("Loan Amount", "loanAmount", data.loanAmount)}
                {renderEditableField("Loan Term", "loanTerm", data.loanTerm)}
                {renderEditableField("Loan Purpose", "loanPurpose", data.loanPurpose)}
                {renderEditableField("Mother's Maiden Name", "motherMaidenName", data.motherMaidenName)}
                {renderEditableField("Referred By", "referredBy", data.referredBy)}
              </>
            ) : (
              <>
                {renderField("Loan Amount", formatCurrency(data.loanAmount))}
                {renderField("Loan Term", data.loanTerm)}
                {renderField("Loan Purpose", data.loanPurpose)}
                {renderField("Mother's Maiden Name", data.motherMaidenName)}
                {renderField("Referred By", data.referredBy)}
              </>
            )}
          </>
        ))}

        {application.civilStatus === 'Married' && renderSection("Spouse Information", "spouse", (data) => (
          <>
            {editMode.spouse ? (
              <>
                {renderEditableField("Name", "spouseName", data.spouseName)}
                {renderEditableField("Employer", "spouseEmployer", data.spouseEmployer)}
                {renderEditableField("Contact", "spouseContact", data.spouseContact)}
              </>
            ) : (
              <>
                {renderField("Name", data.spouseName)}
                {renderField("Employer", data.spouseEmployer)}
                {renderField("Contact", data.spouseContact)}
              </>
            )}
          </>
        ))}

        {renderSection("Co-Maker Information", "coMaker", (data) => (
          <>
            {editMode.coMaker ? (
              <>
                {renderEditableField("First Name", "coMakerFirstName", data.coMakerFirstName)}
                {renderEditableField("Middle Name", "coMakerMiddleName", data.coMakerMiddleName)}
                {renderEditableField("Last Name", "coMakerLastName", data.coMakerLastName)}
                {renderEditableField("Contact", "coMakerContact", data.coMakerContact)}
                {renderEditableField("Email", "coMakerEmail", data.coMakerEmail)}
              </>
            ) : (
              <>
                {renderField("First Name", data.coMakerFirstName)}
                {renderField("Middle Name", data.coMakerMiddleName)}
                {renderField("Last Name", data.coMakerLastName)}
                {renderField("Contact", data.coMakerContact)}
                {renderField("Email", data.coMakerEmail)}
              </>
            )}
          </>
        ))}

        {renderSection("Co-Maker Address", "coMakerAddress", (data) => (
          <>
            {editMode.coMakerAddress ? (
              <>
                {renderEditableField("House/Street", "coMakerHouseStreet", data.coMakerHouseStreet, 6)}
                {renderEditableField("Barangay", "coMakerBarangay", data.coMakerBarangay, 6)}
                {renderEditableField("Municipality", "coMakerMunicipality", data.coMakerMunicipality, 4)}
                {renderEditableField("Province", "coMakerProvince", data.coMakerProvince, 4)}
                {renderEditableField("Zip Code", "coMakerZipCode", data.coMakerZipCode, 4)}
              </>
            ) : (
              <>
                {renderField("House/Street", data.coMakerHouseStreet, 6)}
                {renderField("Barangay", data.coMakerBarangay, 6)}
                {renderField("Municipality", data.coMakerMunicipality, 4)}
                {renderField("Province", data.coMakerProvince, 4)}
                {renderField("Zip Code", data.coMakerZipCode, 4)}
              </>
            )}
          </>
        ))}

        {renderSection("Co-Maker Employment", "coMakerEmployment", (data) => (
          <>
            {editMode.coMakerEmployment ? (
              <>
                {renderEditableField("Source of Income", "coMakerSourceOfIncome", data.coMakerSourceOfIncome)}
                {renderEditableField("Nature of Work", "coMakerNatureOfWork", data.coMakerNatureOfWork)}
                {renderEditableField("Employer", "coMakerEmployer", data.coMakerEmployer)}
                {renderEditableField("Job Position", "coMakerJobPosition", data.coMakerJobPosition)}
                {renderEditableField("Monthly Income", "coMakerMonthlyIncome", data.coMakerMonthlyIncome)}
              </>
            ) : (
              <>
                {renderField("Source of Income", data.coMakerSourceOfIncome)}
                {renderField("Nature of Work", data.coMakerNatureOfWork)}
                {renderField("Employer", data.coMakerEmployer)}
                {renderField("Job Position", data.coMakerJobPosition)}
                {renderField("Monthly Income", formatCurrency(data.coMakerMonthlyIncome))}
              </>
            )}
          </>
        ))}
        <div className="text-center mt-4">
          <StatusButton application={application} onUpdate={onUpdate} showNotification={showNotification} onClose={onHide} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LoanApplicationPage;